import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Upload, FileText, ArrowLeft } from "lucide-react";
import "./ApplicationPage.css";
import emailjs from "@emailjs/browser";
import { supabase } from "../lib/supabase";
import { useRef } from "react";

export default function ApplicationPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    address: "",
    idNumber: "",
    experience: "",
    education: "",
    cvFile: null,
    idFile: null,
    certificatesFile: null
  });

  const formRef = useRef();

  useEffect(() => {
    // Fetch job from database
    const fetchJob = async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching job:', error);
        navigate('/jobs');
      } else {
        setJob(data);
      }
    };

    fetchJob();
  }, [jobId, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, [fieldName]: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // 1️⃣ Upload CV
      const cvName = `${Date.now()}-${formData.cvFile.name}`;
      const { error: cvError } = await supabase.storage
        .from("cvs")
        .upload(cvName, formData.cvFile);
      
      if (cvError) throw new Error("Failed to upload CV: " + cvError.message);

      // 2️⃣ Upload ID (OPTIONAL - only if file exists)
      // let idUrl = null;
      if (formData.idFile) {
        const idName = `${Date.now()}-${formData.idFile.name}`;
        const { error: idError } = await supabase.storage
          .from("ids")
          .upload(idName, formData.idFile);
        
        if (idError) throw new Error("Failed to upload ID: " + idError.message);

        // Get signed URL for ID
        // const { data: idData, error: idUrlError } = await supabase.storage
        //   .from("ids")
        //   .createSignedUrl(idName, 31536000);
        
        // if (!idUrlError) {
        //   idUrl = idData.signedUrl;
        // }
      }

      // 3️⃣ Upload certificates (optional)
      let certUrl = null;
      if (formData.certificatesFile) {
        const certName = `${Date.now()}-${formData.certificatesFile.name}`;
        const { error: certError } = await supabase.storage
          .from("qualifications")
          .upload(certName, formData.certificatesFile);
        
        if (certError) throw new Error("Failed to upload certificate: " + certError.message);

        // Get signed URL for certificate
        const { data: certData, error: certUrlError } = await supabase.storage
          .from("qualifications")
          .createSignedUrl(certName, 31536000);
        
        if (!certUrlError) {
          certUrl = certData.signedUrl;
        }
      }

      // 4️⃣ Get CV signed URL
      const { data: cvData, error: cvUrlError } = await supabase.storage
        .from("cvs")
        .createSignedUrl(cvName, 31536000);
      
      if (cvUrlError) throw new Error("Failed to generate CV link: " + cvUrlError.message);

      const cvUrl = cvData.signedUrl;

      // 5️⃣ Save application to database
      const { data: applicationData, error: dbError } = await supabase
        .from('applications')
        .insert([{
          job_id: jobId,
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          // id_number: removed - column deleted from database
          experience: formData.experience,
          education: formData.education,
          cv_url: cvUrl,
          // id_document_url: removed - column deleted from database
          certificates_url: certUrl, // Will be null if not uploaded
          status: 'Pending'
        }])
        .select();

      if (dbError) throw new Error("Failed to save application: " + dbError.message);

      console.log("Application saved to database:", applicationData);

      // 6️⃣ Send email to HR
      try {
        await emailjs.send(
          "service_vj4h97g",
          "template_ja0szuo",
          {
            fullName: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            experience: formData.experience,
            address: formData.address,
            // idNumber: formData.idNumber,
            education: formData.education,
            cv_link: cvUrl,
            // id_link: idUrl,
            cert_link: certUrl || "None",
            job_title: job.title
          },
          "vWkJpQqvu_4nMnMTT"
        );
        console.log("Email sent successfully to HR");
      } catch (emailError) {
        console.error("Email send failed (but application was saved):", emailError);
        // Don't throw - application is already saved
      }

      alert("Application submitted successfully! We'll review it and get back to you soon.");
      navigate("/jobs");

    } catch (err) {
      console.error("Application submission error:", err);
      
      // Better error message
      let errorMessage = "Error submitting application. ";
      if (err.message) {
        errorMessage += err.message;
      } else if (typeof err === 'string') {
        errorMessage += err;
      } else {
        errorMessage += "Please try again or contact support.";
      }
      
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  if (!job) return <div style={{padding: '40px', textAlign: 'center'}}>Loading job details...</div>;

  return (
    <div className="apply-root">
      <div className="apply-topbar">
        <div className="container topbar-inner">
          <Link to="/jobs" className="back-btn">
            <ArrowLeft size={18} />
            Back to Jobs
          </Link>
        </div>
      </div>

      <div className="container apply-container">
        <div className="apply-header">
          <h2 className="apply-title">Apply for {job.title}</h2>
          <p className="apply-subtitle">{job.location}</p>
          <p className="apply-description">{job.description}</p>
        </div>

        <div className="apply-card">
          <h3 className="form-section-title">Application Form</h3>
          
          <form ref={formRef} onSubmit={handleSubmit} className="apply-form">
            {/* Personal Information */}
            <div className="form-section">
              <h4 className="form-subtitle">Personal Information</h4>
              
              <div className="form-row">
                <div className="form-field">
                  <label className="field-label">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={uploading}
                  />
                </div>
                
                <div className="form-field">
                  <label className="field-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={uploading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label className="field-label">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={uploading}
                  />
                </div>
                
                {/* <div className="form-field">
                  <label className="field-label">ID Number *</label>
                  <input
                    type="text"
                    name="idNumber"
                    value={formData.idNumber}
                    onChange={handleChange}
                    className="form-input"
                    required
                    disabled={uploading}
                  />
                </div> */}
              </div>

              <div className="form-field">
                <label className="field-label">Physical Address *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="2"
                  className="form-textarea"
                  required
                  disabled={uploading}
                />
              </div>
            </div>

            {/* Professional Background */}
            <div className="form-section">
              <h4 className="form-subtitle">Professional Background</h4>
              
              <div className="form-field">
                <label className="field-label">Work Experience *</label>
                <textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Please describe your relevant work experience..."
                  className="form-textarea"
                  required
                  disabled={uploading}
                />
              </div>

              <div className="form-field">
                <label className="field-label">Education *</label>
                <textarea
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  rows="3"
                  placeholder="List your educational qualifications..."
                  className="form-textarea"
                  required
                  disabled={uploading}
                />
              </div>
            </div>

            {/* Document Uploads */}
            <div className="form-section">
              <h4 className="form-subtitle">Required Documents</h4>
              
              <div className="upload-grid">
                <div className="upload-box">
                  <label className="upload-label">
                    <div className="upload-content">
                      <Upload className="upload-icon" />
                      <div>
                        <p className="upload-title">CV/Resume *</p>
                        <p className="upload-subtitle">PDF or Word document</p>
                      </div>
                    </div>
                    {formData.cvFile && (
                      <span className="upload-success">
                        <FileText className="file-icon" />
                        {formData.cvFile.name}
                      </span>
                    )}
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, "cvFile")}
                      accept=".pdf,.doc,.docx"
                      className="upload-input"
                      required
                      disabled={uploading}
                    />
                  </label>
                </div>

                {/* <div className="upload-box">
                  <label className="upload-label">
                    <div className="upload-content">
                      <Upload className="upload-icon" />
                      <div>
                        <p className="upload-title">ID Document *</p>
                        <p className="upload-subtitle">Copy of your ID</p>
                      </div>
                    </div>
                    {formData.idFile && (
                      <span className="upload-success">
                        <FileText className="file-icon" />
                        {formData.idFile.name}
                      </span>
                    )}
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, "idFile")}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="upload-input"
                      required
                      disabled={uploading}
                    />
                  </label>
                </div> */}

                <div className="upload-box">
                  <label className="upload-label">
                    <div className="upload-content">
                      <Upload className="upload-icon" />
                      <div>
                        <p className="upload-title">Certificates (Optional)</p>
                        <p className="upload-subtitle">Educational certificates</p>
                      </div>
                    </div>
                    {formData.certificatesFile && (
                      <span className="upload-success">
                        <FileText className="file-icon" />
                        {formData.certificatesFile.name}
                      </span>
                    )}
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, "certificatesFile")}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="upload-input"
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-button"
                disabled={uploading}
              >
                {uploading ? "Submitting..." : "Submit Application"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/jobs")}
                className="cancel-button"
                disabled={uploading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}